// ─────────────────────────────────────────────────────────────────────
// <MobileBottomTab /> — design vision spec
//
// 5-slot bottom navigation, with Ask S (center) raised + gold ring.
// Visible only on <lg viewports.
// ─────────────────────────────────────────────────────────────────────

import { Link, useLocation } from 'react-router-dom';
import { Compass, Search, Ticket, User, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTenant } from '../../context/TenantContext';   // r7.6

export default function MobileBottomTab() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const tenant = useTenant();   // r7.6 — a tenant app has no marketplace search: the slot shows its events

  const isActive = (path, exact = false) =>
    exact ? pathname === path : pathname === path || pathname.startsWith(path + '/');

  return (
    <nav
      className="lg:hidden fixed bottom-0 inset-x-0 z-30 border-t border-line"
      style={{ background: 'rgba(10,10,11,0.92)', backdropFilter: 'blur(20px)' }}
      aria-label="Bottom"
    >
      <div className="grid grid-cols-5 items-end h-[68px] pb-1">
        <Tab
          to="/"
          label={t('tab.discover', 'Discover')}
          Icon={Compass}
          active={isActive('/', true)}
        />
        {tenant.isPlatform ? (
          <Tab
            to="/search"
            label={t('tab.search', 'Search')}
            Icon={Search}
            active={isActive('/search')}
          />
        ) : (
          <Tab
            to="/v/live-events"
            label={t('nav.events', 'Events')}
            Icon={Calendar}
            active={isActive('/v/live-events') || isActive('/e')}
          />
        )}
        {/* Center raised — Ask S */}
        <CenterAsk active={isActive('/ask')} label={t('tab.ask', 'Ask S')} />
        <Tab
          to="/me/bookings"
          label={t('tab.tickets', 'Tickets')}
          Icon={Ticket}
          active={isActive('/me/bookings')}
        />
        <Tab
          to="/me"
          label={t('tab.profile', 'You')}
          Icon={User}
          active={isActive('/me', true) || pathname === '/me/preferences' || pathname === '/me/wishlist'}
        />
      </div>
    </nav>
  );
}

function Tab({ to, label, Icon, active }) {
  return (
    <Link
      to={to}
      className="flex flex-col items-center justify-end gap-1 no-underline pt-2 pb-1"
      style={{ color: active ? 'var(--snt-accent)' : 'var(--snt-text-3)' }}
    >
      <Icon size={20} strokeWidth={active ? 2 : 1.5} />
      <span
        className="text-[9px] tracking-wide"
        style={{ color: 'inherit' }}
      >
        {label}
      </span>
    </Link>
  );
}

function CenterAsk({ active, label }) {
  return (
    <Link
      to="/ask"
      className="flex flex-col items-center -mt-6 no-underline"
      aria-label={label}
    >
      <span
        className="w-12 h-12 rounded-full flex items-center justify-center font-serif italic"
        style={{
          background: 'var(--snt-accent)',
          color: 'var(--snt-accent-fg)',
          boxShadow: '0 4px 18px color-mix(in srgb, var(--snt-accent) 50%, transparent)',
          fontSize: 22,
          fontWeight: 600,
        }}
        data-glyph="S"
      >
        S
      </span>
      <span
        className="text-[9px] tracking-wide mt-1"
        style={{ color: active ? 'var(--snt-accent)' : 'var(--snt-text-3)' }}
      >
        {label}
      </span>
    </Link>
  );
}
